/**
 * Local-only POC route — drives the real <Teleprompter> with query-string
 * params so a headless browser (Playwright) can screenshot it frame by
 * frame and produce a transparent overlay video.
 *
 * Inputs (query string):
 *   ?text=<urlencoded script>
 *   &maxSec=<float>
 *   &t=<float elapsedSec>
 *   &w=<int>  output viewport width  (default 480)
 *   &h=<int>  output viewport height (default 640)
 *   &band=<float 0..1>  prompter band height as ratio of h (default 0.40)
 *
 * Renders the prompter inside the same gradient band as Record.tsx so the
 * burned-in overlay matches what the destinataire saw during capture.
 * Page background is forced transparent so Playwright `omitBackground`
 * gives us a clean alpha PNG.
 */
import { useEffect, useMemo, useState } from "react";
import Teleprompter from "../components/Teleprompter";
import { distributeTiming, parseScript } from "../utils";

declare global {
  interface Window {
    __setPrompterT?: (t: number) => void;
  }
}

function useQuery() {
  const sp = new URLSearchParams(window.location.search);
  const bi = sp.get("blockIndex");
  return {
    text: sp.get("text") ?? "",
    maxSec: parseFloat(sp.get("maxSec") ?? "20"),
    initialT: parseFloat(sp.get("t") ?? "0"),
    w: parseInt(sp.get("w") ?? "480", 10),
    h: parseInt(sp.get("h") ?? "640", 10),
    band: parseFloat(sp.get("band") ?? "0.40"),
    blockIndex: bi === null || bi === "" ? null : parseInt(bi, 10),
  };
}

export default function DevRenderPrompter() {
  const { text, maxSec, initialT, w, h, band, blockIndex } = useQuery();
  const [elapsedSec, setElapsedSec] = useState(initialT);

  // Expose a global setter so Playwright can drive the page through frames
  // without a full reload (avoids "frame detached" races at 30fps).
  useEffect(() => {
    window.__setPrompterT = (t: number) => setElapsedSec(t);
    return () => {
      delete window.__setPrompterT;
    };
  }, []);

  const { parsed, timing } = useMemo(() => {
    const p = parseScript(text);
    const t = distributeTiming(p.segments, maxSec);
    if (blockIndex == null) return { parsed: p, timing: t };

    // Multi-block render: keep only segments whose block matches and
    // shift their start time so the first one lands at t=0. Filtering
    // on the integer block index (not on start_sec) avoids the float
    // drift the post-process pipeline already had to defend against.
    const keep: { idx: number; tEntry: typeof t[number] }[] = [];
    p.segments.forEach((seg, i) => {
      if (seg.kind === "buffer") return;
      if (seg.blockIndex !== blockIndex) return;
      keep.push({ idx: i, tEntry: t[i] });
    });
    if (keep.length === 0) return { parsed: p, timing: t };
    const shift = keep[0].tEntry.start;
    const filteredSegments = keep.map(({ idx }) => p.segments[idx]);
    const filteredTiming = keep.map(({ tEntry }) => ({
      start: tEntry.start - shift,
      dur: tEntry.dur,
    }));
    return {
      parsed: { intro: p.intro, segments: filteredSegments, blockCount: 1 },
      timing: filteredTiming,
    };
  }, [text, maxSec, blockIndex]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: `${w}px`,
        height: `${h}px`,
        background: "transparent",
        margin: 0,
        padding: 0,
        overflow: "hidden",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/85 via-black/70 to-transparent text-ivory z-10 pt-6"
        style={{ height: `${Math.round(h * band)}px` }}
      >
        <Teleprompter
          intro={parsed.intro}
          segments={parsed.segments}
          timing={timing}
          elapsedSec={elapsedSec}
          phase="recording"
        />
      </div>
    </div>
  );
}
